import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { PublicPageTemplate } from "@/components/public";
import { getCanonicalUrl, LANDING_PAGE_INDUSTRIES } from "@/config/site";
import { createBreadcrumbSchema, createCollectionPageSchema, createOrganizationSchema } from "@/config/seo";
import { getSolutionScenarioBySlug } from "@/data/solutions/registry";
import { UserService } from "@/services/users/UserService";
import { Button } from "@/components/ui/button";
import { SolutionHero } from "./components/SolutionHero";
import { SolutionRealWorldScenarios } from "./components/SolutionRealWorldScenarios";
import { SolutionUseCasesShowcase } from "./components/SolutionUseCasesShowcase";
import { SolutionFinalCTA } from "./components/SolutionFinalCTA";
import { SolutionDemoGenies } from "./components/SolutionDemoGenies";
import HomeUserManual from "@/pages/Home/HomeUserManual";
import { YourGenieYourWay } from "@/pages/Products/AfterHoursPage/components/YourGenieYourWay";
import { getDefaultPageContent } from "./data/defaultPageContent";

// Map market slugs to their hero images
const marketHeroImages: Record<string, string> = {
  'automotive': '/solutions-pages/automotive/hero.webp',
  'marine': '/solutions-pages/marine/hero.webp',
  'appliances': '/solutions-pages/appliances/hero.webp',
  'office-equipment': '/solutions-pages/office-equipment/hero.webp',
  'equipment': '/solutions-pages/equipment/hero.webp',
  'home-builders': '/solutions-pages/home-builders/hero.webp',
  'travel-hospitality': '/solutions-pages/travel-hospitality/hero.webp',
  'manufacturing': '/solutions-pages/manufacturing/hero.webp',
  'industrial': '/solutions-pages/industrial/hero.webp',
  'mining': '/solutions-pages/mining/hero.webp',
  'trades': '/solutions-pages/trades/hero.webp',
  'real-estate': '/solutions-pages/real-estate/hero.webp',
  'clubs-associations': '/solutions-pages/clubs-associations/hero.webp',
  'events': '/solutions-pages/events/hero.webp',
};

const PublicSolutionPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Get solution scenario data
  const scenario = getSolutionScenarioBySlug(slug || "");

  // Industries with a static Astro landing page are 301'd at the Netlify
  // edge already; this client-side fallback catches any request that
  // reaches the SPA anyway (previews, stale cached links) so we never
  // render a duplicate of a page that already exists elsewhere.
  if (slug && LANDING_PAGE_INDUSTRIES.has(slug)) {
    if (typeof window !== "undefined") {
      // Prefer a hard navigation so crawlers that execute JS still land on
      // the Astro landing page URL (and its self-canonical).
      window.location.replace(`/${slug}/`);
      return null;
    }
    return <Navigate to={`/${slug}/`} replace />;
  }

  if (!scenario) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Solution not found</h2>
          <Button onClick={() => navigate("/solutions")}>Back to Solutions</Button>
        </div>
      </div>
    );
  }

  const handleGetStarted = async () => {
    const {
      data: { user },
    } = await UserService.getUser();
    if (!user) {
      navigate("/signin");
      return;
    }
    navigate("/genie-hub/genie-setup");
  };

  const handleTalkToMe = () => {
    navigate("/live-agent/business-onboarding");
  };

  // Get market-specific hero image
  const heroImage = marketHeroImages[scenario.meta.slug] || '/seamless-integration.webp';

  // Get page content - use scenario's pageContent if available, otherwise use defaults
  const defaultContent = getDefaultPageContent(scenario.meta.market);
  const pageContent = scenario.pageContent || defaultContent;

  const seoConfig = {
    title: `${scenario.meta.name} Voice Solutions | Help Genie`,
    description: scenario.meta.description,
    ogTitle: `${scenario.meta.name} Voice Solutions`,
    ogDescription: scenario.meta.description,
    canonicalUrl: getCanonicalUrl(`/solutions/${scenario.meta.slug}`),
    structuredData: [
      createBreadcrumbSchema([
        { name: "Home", url: getCanonicalUrl("/") },
        { name: "Solutions", url: getCanonicalUrl("/solutions") },
        { name: scenario.meta.name, url: getCanonicalUrl(`/solutions/${scenario.meta.slug}`) },
      ]),
      createCollectionPageSchema(scenario.meta.name, scenario.meta.description, getCanonicalUrl(`/solutions/${scenario.meta.slug}`)),
      createOrganizationSchema(),
    ],
  };

  return (
    <PublicPageTemplate seo={seoConfig}>
      {/* Hero */}
      <SolutionHero
        scenario={scenario}
        heroImage={heroImage}
        onGetStarted={handleGetStarted}
        onTalkToMe={handleTalkToMe}
        isCreating={isCreating}
      />

      {/* Real World Scenarios - Interactive chat demo */}
      {pageContent.chatScenarios && pageContent.chatScenarios.length > 0 && (
        <SolutionRealWorldScenarios
          title={pageContent.scenariosHeader?.title}
          subtitle={pageContent.scenariosHeader?.subtitle}
          scenarios={pageContent.chatScenarios}
        />
      )}

      {/* Try a Genie Demo - Industry-specific clickable phone mockups */}
      <SolutionDemoGenies
        industrySlug={scenario.meta.slug}
        industryName={scenario.meta.name}
      />

      {/* Use Cases Showcase - 2x2 grid */}
      {pageContent.useCaseCards && pageContent.useCaseCards.length > 0 && (
        <SolutionUseCasesShowcase
          title={pageContent.useCasesHeader?.title}
          subtitle={pageContent.useCasesHeader?.subtitle}
          useCases={pageContent.useCaseCards}
        />
      )}

      {/* How It Works */}
      <HomeUserManual backgroundImage={`/solutions-pages/${scenario.meta.slug}/how-it-works-bg.webp`} />

      {/* Your Genie Your Way - Q&A style with images */}
      <YourGenieYourWay
        market={scenario.meta.slug}
        subtitle={`Everything you need to know about making your ${scenario.meta.name} Genie work exactly how you want it.`}
      />

      {/* Final CTA */}
      {pageContent.finalCTA && pageContent.finalCTA.pillars.length > 0 && (
        <SolutionFinalCTA
          title={pageContent.finalCTA.title}
          subtitle={pageContent.finalCTA.subtitle}
          pillars={pageContent.finalCTA.pillars}
          backgroundImage={pageContent.finalCTA.backgroundImage || heroImage}
        />
      )}
    </PublicPageTemplate>
  );
};

export default PublicSolutionPage;
